import React, { useState, useEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Typography,
  Box,
  TextField,
  Switch,
  Button,
  Paper,
  Alert,
  Rating,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import {
  Message as MessageIcon,
  Palette as PaletteIcon,
  Feedback as FeedbackIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Star as StarIcon,
  SentimentSatisfiedAlt as EmojiIcon,
  ThumbUp as ThumbUpIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

// Styled Components - Matching DisplayOptions.js/TextColors.js pattern
const ConfigPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SettingsToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
}));

const ColorPickerRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ColorSwatch = styled(Box)(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor: color,
  border: "2px solid rgba(0,0,0,0.1)",
  flexShrink: 0,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

// Rating Type Cards
const RatingTypeCard = styled(Box)(({ theme, selected }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  borderRadius: 12,
  cursor: "pointer",
  transition: "all 0.2s ease",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : "2px solid transparent",
  backgroundColor: selected
    ? theme.palette.primary.lighter || "rgba(25, 118, 210, 0.08)"
    : theme.palette.grey[50],
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.lighter || "rgba(25, 118, 210, 0.12)"
      : theme.palette.grey[100],
    transform: "translateY(-2px)",
  },
  position: "relative",
  minWidth: 100,
}));

const RatingTypeIconBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
  fontSize: "1.5rem",
}));

// Preview Components
const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f5f7fa",
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
}));

const ChatWindowPreview = styled(Box)({
  maxWidth: 350,
  backgroundColor: "#ffffff",
  borderRadius: 16,
  boxShadow: "0 5px 40px rgba(0,0,0,0.15)",
  overflow: "hidden",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  height: 520,
});

const ChatHeaderPreview = styled(Box)(({ buttoncolor, fontcolor }) => ({
  backgroundColor: buttoncolor || "#4CAF50",
  color: fontcolor || "#ffffff",
  padding: "16px",
  fontWeight: 600,
  fontSize: "1rem",
  display: "flex",
  alignItems: "center",
  gap: 12,
}));

const SessionEndBodyPreview = styled(Box)(({ bgcolor, textcolor }) => ({
  flex: 1,
  padding: 20,
  backgroundColor: bgcolor || "#f0f4f8",
  color: textcolor || "#000000",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  textAlign: "center",
  overflowY: "auto",
}));

const FeedbackTextAreaPreview = styled(TextField)({
  marginTop: 10,
  marginBottom: 10,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: 12,
  },
});

const SubmitButtonPreview = styled(Button)({
  marginTop: 10,
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 600,
});

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
    }
  }
`;

// Rating type options
const ratingTypeOptions = [
  {
    value: "stars",
    label: "Stars",
    icon: <StarIcon sx={{ color: "#FFB400", fontSize: 28 }} />,
    preview: "⭐⭐⭐⭐⭐",
  },
  {
    value: "emoji",
    label: "Emoji",
    icon: <EmojiIcon sx={{ color: "#4CAF50", fontSize: 28 }} />,
    preview: "😞😐😊😄🤩",
  },
  {
    value: "thumbs",
    label: "Thumbs",
    icon: <ThumbUpIcon sx={{ color: "#2196F3", fontSize: 28 }} />,
    preview: "👎 👍",
  },
];

function SessionEndScreen({
  handleChildData,
  widgetData,
  isEditing,
  widgetId,
  onSave,
}) {
  const [updateWidget] = useMutation(UPDATE_WIDGET);

  // State declarations
  const [title, setTitle] = useState("Thank you!");
  const [closingMessage, setClosingMessage] = useState(
    "I hope I could help you ..."
  );
  const [backgroundColor, setBackgroundColor] = useState("#f0f4f8");
  const [fontColor, setFontColor] = useState("#000000");
  const [showFeedbackForm, setShowFeedbackForm] = useState(true);
  const [ratingType, setRatingType] = useState("stars");
  const [allowMessageFeedback, setAllowMessageFeedback] = useState(true);

  // Get widget theme colors for preview header
  const widgetButtonColor =
    widgetData?.welcome_screen?.button_color || "#4CAF50";
  const widgetFontColor = widgetData?.welcome_screen?.font_color || "#ffffff";
  const businessName =
    widgetData?.business_details?.display_name || "Support Chat";

  // Initialize state from widgetData when editing
  useEffect(() => {
    if (widgetData?.session_end) {
      setTitle(widgetData.session_end.title || "Thank you!");
      setClosingMessage(
        widgetData.session_end.closing_message || "I hope I could help you ..."
      );
      setBackgroundColor(widgetData.session_end.background_color || "#f0f4f8");
      setFontColor(widgetData.session_end.text_color || "#000000");
      setShowFeedbackForm(widgetData.session_end.show_feedback_form ?? true);
      setRatingType(widgetData.session_end.rating_type || "stars");
    }
  }, [widgetData]);

  const handleSaveSessionEndScreen = useCallback(async () => {
    if (!widgetId) {
      console.error("No widgetId available");
      return;
    }

    try {
      const widgetInput = {
        business_details: widgetData?.business_details
          ? {
              business_type: widgetData.business_details.business_type,
              name: widgetData.business_details.name,
              display_name: widgetData.business_details.display_name,
              website_url: widgetData.business_details.website_url,
            }
          : undefined,
        website_integration: widgetData?.website_integration
          ? {
              mode: widgetData.website_integration.mode,
              button_style: widgetData.website_integration.button_style,
              position: widgetData.website_integration.position,
              square_buttom: widgetData.website_integration.square_buttom,
              minimize_small_button:
                widgetData.website_integration.minimize_small_button,
              preview: widgetData.website_integration.preview,
              media: widgetData.website_integration.media,
              picture: widgetData.website_integration.picture,
              speeach_bubble_test:
                widgetData.website_integration.speeach_bubble_test,
              call_to_action_button_text:
                widgetData.website_integration.call_to_action_button_text,
              slogan: widgetData.website_integration.slogan,
            }
          : undefined,
        welcome_screen: widgetData?.welcome_screen
          ? {
              font_shadow: widgetData.welcome_screen.font_shadow,
              desc: widgetData.welcome_screen.desc,
              customizable_button:
                widgetData.welcome_screen.customizable_button,
              button_color: widgetData.welcome_screen.button_color,
              font_color: widgetData.welcome_screen.font_color,
              icon_color: widgetData.welcome_screen.icon_color,
              welcome_message: widgetData.welcome_screen.welcome_message,
            }
          : undefined,
        // Preserve contact_options and request_routing from previous step
        contact_options: widgetData?.contact_options
          ? {
              chat_enabled: widgetData.contact_options.chat_enabled,
              phone_enabled: widgetData.contact_options.phone_enabled,
              video_enabled: widgetData.contact_options.video_enabled,
              audio_enabled: widgetData.contact_options.audio_enabled,
            }
          : undefined,
        request_routing: widgetData?.request_routing
          ? {
              preselection_enabled:
                widgetData.request_routing.preselection_enabled,
              automatic_response_enabled:
                widgetData.request_routing.automatic_response_enabled,
              automatic_response_text:
                widgetData.request_routing.automatic_response_text,
              lead_form_enabled: widgetData.request_routing.lead_form_enabled,
              privacy_enabled: widgetData.request_routing.privacy_enabled,
              privacy_text: widgetData.request_routing.privacy_text,
            }
          : undefined,
        // Save session end screen settings (Step 4)
        session_end: {
          title: title,
          closing_message: closingMessage,
          background_color: backgroundColor,
          text_color: fontColor,
          show_feedback_form: showFeedbackForm,
          rating_type: ratingType,
        },
        // Preserve contact_form and visibility_settings
        contact_form: widgetData?.contact_form
          ? {
              unavailable_message: widgetData.contact_form.unavailable_message,
              background_color: widgetData.contact_form.background_color,
              text_color: widgetData.contact_form.text_color,
              form_fields: widgetData.contact_form.form_fields?.map(
                ({ __typename, ...field }) => field
              ),
              privacy_enabled: widgetData.contact_form.privacy_enabled,
              privacy_text: widgetData.contact_form.privacy_text,
            }
          : undefined,
        visibility_settings: widgetData?.visibility_settings
          ? {
              always_on: widgetData.visibility_settings.always_on,
              custom_schedule_enabled:
                widgetData.visibility_settings.custom_schedule_enabled,
              timezone: widgetData.visibility_settings.timezone,
              schedule: widgetData.visibility_settings.schedule,
              hide_on_mobile: widgetData.visibility_settings.hide_on_mobile,
              hide_when_offline:
                widgetData.visibility_settings.hide_when_offline,
              dialog_delay_seconds:
                widgetData.visibility_settings.dialog_delay_seconds,
            }
          : undefined,
      };

      const { data } = await updateWidget({
        variables: { id: widgetId, input: widgetInput },
      });

      console.log("Session end screen saved successfully");
      return { widgetId, widgetData: data.updateWidget };
    } catch (err) {
      console.error("Error saving session end screen:", err);
      throw err;
    }
  }, [
    widgetId,
    widgetData,
    title,
    closingMessage,
    backgroundColor,
    fontColor,
    showFeedbackForm,
    ratingType,
    updateWidget,
  ]);

  // Expose save function to parent component
  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveSessionEndScreen);
    }
  }, [onSave, handleSaveSessionEndScreen]);

  // Render rating preview based on type
  const renderRatingPreview = () => {
    switch (ratingType) {
      case "stars":
        return (
          <Rating
            value={4}
            readOnly
            size="large"
            sx={{
              "& .MuiRating-iconFilled": {
                color: "#FFB400",
              },
            }}
          />
        );
      case "emoji":
        return (
          <Box sx={{ display: "flex", gap: 1, fontSize: "1.5rem" }}>
            <span style={{ opacity: 0.5 }}>😞</span>
            <span style={{ opacity: 0.5 }}>😐</span>
            <span style={{ opacity: 0.5 }}>😊</span>
            <span style={{ opacity: 1 }}>😄</span>
            <span style={{ opacity: 0.5 }}>🤩</span>
          </Box>
        );
      case "thumbs":
        return (
          <Box sx={{ display: "flex", gap: 3 }}>
            <ThumbUpIcon
              sx={{
                fontSize: 32,
                color: "#4CAF50",
                cursor: "pointer",
                transform: "rotate(180deg)",
                opacity: 0.5,
              }}
            />
            <ThumbUpIcon
              sx={{ fontSize: 32, color: "#4CAF50", cursor: "pointer" }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Grid container spacing={4}>
      {/* Header Section */}
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Configure Session End Screen
        </Typography>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Please create all your texts in English or leave the default texts.
          You can manage the translations in Translations. We always show the
          widget in the browser language the user has chosen. If this is not
          available, English will be default.
        </Alert>
      </Grid>

      {/* Configuration Panel */}
      <Grid item xs={12} md={5}>
        <ConfigPanel elevation={3}>
          {/* Section 1: Closing Message */}
          <Section>
            <SectionTitle variant="h6">
              <MessageIcon color="primary" />
              Closing Message
            </SectionTitle>

            <Box mb={2}>
              <StyledTextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                helperText="Main heading displayed at session end"
                size="small"
              />
            </Box>

            <Box>
              <StyledTextField
                fullWidth
                label="Closing Message"
                multiline
                rows={3}
                value={closingMessage}
                onChange={(e) => setClosingMessage(e.target.value)}
                helperText="Thank you message shown to customers"
                size="small"
              />
            </Box>
          </Section>

          {/* Section 2: Colors */}
          <Section>
            <SectionTitle variant="h6">
              <PaletteIcon color="primary" />
              Screen Colors
            </SectionTitle>

            <ColorPickerRow>
              <ColorSwatch color={backgroundColor} />
              <MuiColorInput
                format="hex"
                value={backgroundColor}
                onChange={setBackgroundColor}
                label="Background Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block", ml: 7 }}
            >
              Background color for the session end screen
            </Typography>

            <ColorPickerRow>
              <ColorSwatch color={fontColor} />
              <MuiColorInput
                format="hex"
                value={fontColor}
                onChange={setFontColor}
                label="Text Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", ml: 7 }}
            >
              Color for all text on the session end screen
            </Typography>
          </Section>

          {/* Section 3: Feedback Form */}
          <Section>
            <SectionTitle variant="h6">
              <FeedbackIcon color="primary" />
              Feedback Form
            </SectionTitle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Enable Feedback Form
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Collect customer feedback after session ends
                </Typography>
              </Box>
              <Switch
                checked={showFeedbackForm}
                onChange={(e) => setShowFeedbackForm(e.target.checked)}
                color="primary"
              />
            </SettingsToggle>

            {showFeedbackForm && (
              <Box
                sx={{
                  mt: 3,
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                {/* Rating Type Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
                    Rating Type
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {ratingTypeOptions.map((option) => (
                      <RatingTypeCard
                        key={option.value}
                        selected={ratingType === option.value}
                        onClick={() => setRatingType(option.value)}
                      >
                        {ratingType === option.value && (
                          <CheckIcon
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              fontSize: 16,
                              color: "primary.main",
                            }}
                          />
                        )}
                        <RatingTypeIconBox>{option.icon}</RatingTypeIconBox>
                        <Typography variant="caption" fontWeight={500}>
                          {option.label}
                        </Typography>
                      </RatingTypeCard>
                    ))}
                  </Box>
                </Box>

                {/* Message Feedback Toggle */}
                <SettingsToggle>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Allow Text Feedback
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Let customers write detailed feedback
                    </Typography>
                  </Box>
                  <Switch
                    checked={allowMessageFeedback}
                    onChange={(e) => setAllowMessageFeedback(e.target.checked)}
                    color="primary"
                  />
                </SettingsToggle>
              </Box>
            )}
          </Section>
        </ConfigPanel>
      </Grid>

      {/* Preview Panel */}
      <Grid item xs={12} md={7}>
        <PreviewContainer elevation={3}>
          <SectionTitle variant="h6" sx={{ mb: 2 }}>
            <ChatIcon color="primary" />
            Live Preview
          </SectionTitle>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ChatWindowPreview>
              {/* Chat Header - Uses widget theme colors */}
              <ChatHeaderPreview
                buttoncolor={widgetButtonColor}
                fontcolor={widgetFontColor}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChatIcon sx={{ color: widgetFontColor, fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: widgetFontColor }}
                  >
                    {businessName}
                  </Typography>
                </Box>
                <CloseIcon
                  sx={{ color: widgetFontColor, opacity: 0.7, fontSize: 20 }}
                />
              </ChatHeaderPreview>

              {/* Session End Body - Uses local state colors */}
              <SessionEndBodyPreview
                bgcolor={backgroundColor}
                textcolor={fontColor}
              >
                {/* Title */}
                {title && (
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: fontColor, mt: 2 }}
                  >
                    {title}
                  </Typography>
                )}

                {/* Closing Message */}
                {closingMessage && (
                  <Typography variant="body1" sx={{ color: fontColor }}>
                    {closingMessage}
                  </Typography>
                )}

                {/* Rating Preview */}
                {showFeedbackForm && (
                  <Box sx={{ my: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: fontColor, mb: 1, fontWeight: 500 }}
                    >
                      How was your experience?
                    </Typography>
                    {renderRatingPreview()}
                  </Box>
                )}

                {/* Feedback Text Area */}
                {(showFeedbackForm || allowMessageFeedback) &&
                  allowMessageFeedback && (
                    <Box sx={{ width: "100%", textAlign: "left" }}>
                      <Typography
                        variant="body2"
                        sx={{ color: fontColor, fontWeight: 500, mb: 1 }}
                      >
                        Please leave your feedback:
                      </Typography>
                      <FeedbackTextAreaPreview
                        multiline
                        rows={2}
                        variant="outlined"
                        placeholder="Type your feedback here..."
                        fullWidth
                        size="small"
                      />
                      <SubmitButtonPreview
                        variant="contained"
                        fullWidth
                        sx={{
                          backgroundColor: widgetButtonColor,
                          "&:hover": {
                            backgroundColor: widgetButtonColor,
                            opacity: 0.9,
                          },
                        }}
                      >
                        Submit Feedback
                      </SubmitButtonPreview>
                    </Box>
                  )}
              </SessionEndBodyPreview>
            </ChatWindowPreview>
          </Box>
        </PreviewContainer>
      </Grid>
    </Grid>
  );
}

export default SessionEndScreen;
